import sumBy from 'lodash/sumBy';
import type { Icon } from '@phosphor-icons/react';
import { ArrowsClockwise, Leaf, Suitcase, ShieldCheck } from '@phosphor-icons/react';
import { constants } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import {
  cancelForAnyReason,
  checkInBaggage,
  climateCompensation,
  flexibleTicket,
  cancellationGuarantee,
} from '@eti/products';
import { PRESELECTION_VALUES } from '../../../../../constants/productConstants';
import { getIncludedBaggageText, usePriceDisplayType } from '../../../utils/resultUtils';
import type { AvailableExtraProductsType, AvailableExtraProductType } from '../../../models';

const { TOTAL } = constants.priceDisplayTypes;

const productIDsAvailableForPreSelection: number[] = [
  cancelForAnyReason.id,
  cancellationGuarantee.id,
  checkInBaggage.id,
  climateCompensation.id,
  flexibleTicket.id,
];

const getPreSelectedExtraProducts = (availableExtraProducts: AvailableExtraProductsType) =>
  availableExtraProducts?.filter((availableExtraProduct: AvailableExtraProductType) => {
    const { productId } = availableExtraProduct;
    return (
      availableExtraProduct.configuration?.productPreSelection === PRESELECTION_VALUES.yes &&
      productIDsAvailableForPreSelection.includes(Number(productId))
    );
  });

const usePreSelectedExtraProducts = (
  availableExtraProducts: AvailableExtraProductsType,
  numberOfTravelers: number,
) => {
  const { t } = useTranslation();

  const { priceDisplayType } = usePriceDisplayType();
  const showTripPriceInTotal = priceDisplayType === TOTAL;

  const getPriceByDisplayType = (totalPrice: number) =>
    showTripPriceInTotal ? totalPrice : totalPrice / numberOfTravelers;

  const preSelectedExtraProducts = getPreSelectedExtraProducts(availableExtraProducts)?.filter(
    ({ productId }) => {
      return productIDsAvailableForPreSelection.includes(Number(productId));
    },
  );

  return preSelectedExtraProducts
    ?.map(({ productId, name, sellSpecification }) => {
      const currentId = Number(productId);
      let productName = name;
      let totalPrice = 0;
      let iconComponent: Icon = ArrowsClockwise;
      switch (currentId) {
        case flexibleTicket.id:
          if (sellSpecification.__typename === 'SellSpecificationTravelerNew') {
            totalPrice = sumBy(sellSpecification.sellPriceTravelers, 'price.price.value');
            iconComponent = ArrowsClockwise;
          }
          break;

        case cancelForAnyReason.id:
          totalPrice = sumBy([sellSpecification], 'price.price.value');
          iconComponent = ArrowsClockwise;
          break;

        case climateCompensation.id:
          if (sellSpecification.__typename === 'SellSpecificationNoneNew') {
            totalPrice = sellSpecification.price.price.value;
            iconComponent = Leaf;
          }
          break;

        case cancellationGuarantee.id:
          if (sellSpecification.__typename === 'SellSpecificationNoneNew') {
            totalPrice = sellSpecification.price.price.value;
            iconComponent = ShieldCheck;
          }
          break;

        case checkInBaggage.id:
          if (sellSpecification.__typename === 'SellSpecificationBaggageNew') {
            const { options = [] } = sellSpecification.sellPriceBaggage[0] ?? {};
            const { weight = 0, numberOfUnits = 0, weightUnit = '' } = options[0] ?? {};
            const pieces = showTripPriceInTotal ? sumBy(options, 'numberOfUnits') : numberOfUnits;

            const includedBaggageText: string = getIncludedBaggageText(
              { weight, pieces, weightUnit },
              'IbeClient.BaggageIncluded.Label',
              t,
            );
            const totalBaggagePriceForAllTravelers = sumBy(
              sellSpecification.sellPriceBaggage?.flatMap(
                (baggageOptions) => baggageOptions?.options,
              ),
              'price.price.value',
            );

            productName = includedBaggageText;
            totalPrice = totalBaggagePriceForAllTravelers;
            iconComponent = Suitcase;
          }
          break;

        default:
          break;
      }
      if (productIDsAvailableForPreSelection.includes(currentId)) {
        return {
          productId: currentId,
          name: productName,
          price: getPriceByDisplayType(totalPrice),
          totalPrice,
          IconComponent: iconComponent,
        };
      }
      return null;
    })
    .flatMap((product) => (product ? [product] : []));
};

export default usePreSelectedExtraProducts;
